import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { LoadingBoxComponent } from '@shared/components/loading-box/loading-box.component';
import { TimelineHistoricoComponent, TimelineHistoricoItem } from '@shared/components/timeline-historico/timeline-historico.component';
import { OrigemOcorrenciaEnum } from '@shared/enums/repo-dfe/origem-ocorrencia.enum';
import { PermissaoEnum } from '@shared/enums/usuario/permissao.enum';
import { classeBadgeStatusNFeEntrada } from '@shared/helpers/nota-fiscal-entrada-status.helper';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { NotaFiscalEntradaService } from '@shared/services/apis/orquestrador-compras/nota-fiscal-entrada.service';
import { AuthService } from '@shared/services/apis/usuario/auth.service';
import { UsuarioService } from '@shared/services/apis/usuario/usuario.service';
import { CriarNotaFiscalEntradaOcorrenciaRequest } from '@shared/services/requests/orquestrador-compras/criar-nota-fiscal-entrada-ocorrencia.request';
import { NFeEntradaOcorrenciaStruct } from '@shared/services/structs/repo-dfe/nfe-entrada-ocorrencia.struct';
import { HistoricoNotaFiscalEntradaDialogData } from './historico-nota-fiscal-entrada-dialog-data';
import { dataParaDiaCivil, obterDataAtualDaEmpresa, obterInicioDoDiaDaEmpresa } from '@shared/helpers/datetime.util';

/**
 * "Ver histórico" da NF-e de entrada: as ocorrências da nota em `app-timeline-historico`, com filtros e,
 * para quem pode alterar a nota, a anotação manual.
 */
@Component({
  selector: 'app-historico-nota-fiscal-entrada-dialog',
  standalone: true,
  imports: [MatDialogTitle, MatDialogContent, MatDialogActions, MatButtonModule, MatIconModule, MatProgressSpinnerModule, LoadingBoxComponent, TimelineHistoricoComponent],
  templateUrl: './historico-nota-fiscal-entrada-dialog.component.html',
  styleUrls: ['./historico-nota-fiscal-entrada-dialog.component.css'],
})
export class HistoricoNotaFiscalEntradaDialogComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<HistoricoNotaFiscalEntradaDialogComponent>);
  private readonly notaFiscalEntradaService = inject(NotaFiscalEntradaService);
  private readonly authService = inject(AuthService);
  private readonly usuarioService = inject(UsuarioService);
  private readonly alertService = inject(AlertService);
  readonly data = inject<HistoricoNotaFiscalEntradaDialogData>(MAT_DIALOG_DATA);

  @ViewChild(TimelineHistoricoComponent) timeline?: TimelineHistoricoComponent;

  readonly podeAnotar = (this.authService.getUserInfos()?.listaPermissoes ?? []).includes(
    PermissaoEnum.Suprimentos_NotasFiscaisEntrada_Alterar
  );
  isLoading = true;
  isRegistrandoAnotacao = false;
  itens: TimelineHistoricoItem[] = [];
  /** Nome por id, para as linhas migradas do histórico antigo, que não guardavam o nome do usuário. */
  private readonly usuarios = new Map<number, string>();

  ngOnInit(): void {
    this.carregar();
  }

  /** O skeleton é só da primeira carga; depois de anotar, a lista troca no lugar. */
  private carregar(): void {
    forkJoin({
      res: this.notaFiscalEntradaService.getHistorico(this.data.idNFeEntrada),
      usuarios: this.usuarios.size > 0 ? of(null) : this.usuarioService.getAll().pipe(catchError(() => of(null))),
    }).subscribe({
      next: ({ res, usuarios }) => {
        this.isLoading = false;
        usuarios?.listaUsuario?.forEach(u => this.usuarios.set(u.idUsuario, u.nomeUsuario));
        if (res.isError) {
          this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
          return;
        }
        this.itens = (res.listaOcorrencia ?? []).map(ocorrencia => this.paraItem(ocorrencia));
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Erro', 'Não foi possível carregar o histórico.', AlertType.error);
      },
    });
  }

  /** Sem nome gravado, o autor vem da lista de usuários; sem usuário, é a origem; a origem só aparece como badge quando não é o próprio usuário. */
  private paraItem(ocorrencia: NFeEntradaOcorrenciaStruct): TimelineHistoricoItem {
    return {
      icone: ocorrencia.nomeIconeTipoOcorrencia,
      titulo: ocorrencia.nomeTipoOcorrencia,
      descricao: ocorrencia.descricao,
      detalhe: ocorrencia.detalhes,
      justificativa: ocorrencia.justificativa,
      dados: ocorrencia.dados,
      quando: ocorrencia.dataOcorrencia,
      quemNome:
        ocorrencia.nomeUsuario ||
        (ocorrencia.idUsuario != null ? this.usuarios.get(ocorrencia.idUsuario) : null) ||
        ocorrencia.nomeOrigemOcorrencia,
      nomeOrigem: ocorrencia.nomeOrigemOcorrencia,
      iconeOrigem: ocorrencia.nomeIconeOrigemOcorrencia,
      exibirOrigem: ocorrencia.idOrigemOcorrencia !== OrigemOcorrenciaEnum.Usuario,
      nomeStatusAnterior: ocorrencia.nomeStatusNFeEntradaAnterior,
      classeStatusAnterior:
        ocorrencia.idStatusNFeEntradaAnterior != null ? classeBadgeStatusNFeEntrada(ocorrencia.idStatusNFeEntradaAnterior) : null,
      nomeStatusAtual: ocorrencia.nomeStatusNFeEntradaAtual,
      classeStatusAtual:
        ocorrencia.idStatusNFeEntradaAtual != null ? classeBadgeStatusNFeEntrada(ocorrencia.idStatusNFeEntradaAtual) : null,
    };
  }

  /** Anotação do dia vai sem data, para o servidor gravar a hora do registro; retroativa leva o dia escolhido. */
  registrarAnotacao(anotacao: { descricao: string; dataOcorrencia: Date }): void {
    const request = new CriarNotaFiscalEntradaOcorrenciaRequest();
    request.descricao = anotacao.descricao;
    const timezone = this.authService.getUserInfos().timezone;
    request.dataOcorrencia = dataParaDiaCivil(anotacao.dataOcorrencia) === dataParaDiaCivil(obterDataAtualDaEmpresa(timezone))
      ? null
      : obterInicioDoDiaDaEmpresa(anotacao.dataOcorrencia, timezone);

    this.isRegistrandoAnotacao = true;
    this.notaFiscalEntradaService.criarOcorrencia(this.data.idNFeEntrada, request).subscribe({
      next: res => {
        this.isRegistrandoAnotacao = false;
        if (res.isError) {
          this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
          return;
        }
        this.timeline?.limparAnotacao();
        this.alertService.show('Sucesso', 'Anotação registrada.', AlertType.success);
        this.carregar();
      },
      error: () => {
        this.isRegistrandoAnotacao = false;
        this.alertService.show('Erro', 'Não foi possível registrar a anotação.', AlertType.error);
      },
    });
  }

  fechar(): void {
    this.dialogRef.close();
  }
}
